/**
 * MeetingList Component
 * Displays a list of meetings grouped by upcoming and past
 */

import { CalendarClock, CalendarDays, Plus } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { groupMeetingsByTime } from "@/lib/dateUtils";
import type { Meeting } from "@/types/meeting.types";
import { MeetingCard } from "./MeetingCard";

interface MeetingListProps {
  meetings: Meeting[];
  onSchedule?: () => void;
  onEdit?: (meeting: Meeting) => void;
  onDelete?: (meeting: Meeting) => void;
  onComplete?: (meeting: Meeting) => void;
  onCancel?: (meeting: Meeting) => void;
  onClick?: (meeting: Meeting) => void;
  isLoading?: boolean;
}

export function MeetingList({
  meetings,
  onSchedule,
  onEdit,
  onDelete,
  onComplete,
  onCancel,
  onClick,
  isLoading = false,
}: MeetingListProps) {
  const [activeTab, setActiveTab] = useState<"upcoming" | "past">("upcoming");

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading meetings...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { upcoming, past } = groupMeetingsByTime(meetings);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            Meetings
            <Badge variant="secondary" className="ml-2">
              {upcoming.length} upcoming
            </Badge>
          </CardTitle>
          {onSchedule && (
            <Button size="sm" onClick={onSchedule}>
              <Plus className="h-4 w-4 mr-2" />
              Schedule Event
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {meetings.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="mx-auto w-16 h-16 bg-muted/30 rounded-full flex items-center justify-center mb-4">
              <CalendarClock className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Meetings Scheduled</h3>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              Schedule your first meeting with this customer to keep track of consultations and
              follow-ups.
            </p>
            {onSchedule && (
              <Button onClick={onSchedule}>
                <Plus className="h-4 w-4 mr-2" />
                Schedule First Event
              </Button>
            )}
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "upcoming" | "past")}>
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="upcoming" className="relative">
                Upcoming
                {upcoming.length > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 min-w-5 px-1.5 text-xs">
                    {upcoming.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="past" className="relative">
                Past
                {past.length > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 min-w-5 px-1.5 text-xs">
                    {past.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Upcoming Meetings */}
            <TabsContent value="upcoming" className="space-y-3">
              {upcoming.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarClock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No upcoming meetings</p>
                </div>
              ) : (
                upcoming.map((meeting) => (
                  <MeetingCard
                    key={meeting.id}
                    meeting={meeting}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onComplete={onComplete}
                    onCancel={onCancel}
                    onClick={onClick}
                  />
                ))
              )}
            </TabsContent>

            {/* Past Meetings */}
            <TabsContent value="past" className="space-y-3">
              {past.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarDays className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No past meetings</p>
                </div>
              ) : (
                past.map((meeting) => (
                  <MeetingCard
                    key={meeting.id}
                    meeting={meeting}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onClick={onClick}
                    showActions={false}
                  />
                ))
              )}
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}
